import { useRef, useState } from "react";
import { signInWithEmployeeId } from "../api";
import { S } from "../styles";
import { getErrorMessage } from "../api";
import { activateTapGuardedField, getTapGuardedHandlers } from "../ui";

export function SetupScreen() {
  return (
    <div style={S.loginShell}>
      <div style={S.loginCard}>
        <div style={S.loginBanner}>
          <div style={S.pageEyebrow}>COMPANY NAME</div>
          <div style={{ ...S.pageTitle, marginTop: 8 }}>
            Phase 3 is coded, but Supabase is not configured yet.
          </div>
          <div style={{ ...S.pageBody, marginTop: 10 }}>
            Add your project credentials to <code>.env</code>, then restart the dev
            server.
          </div>
        </div>

        <div style={S.sectionTitle}>Required Variables</div>
        <div style={S.listCard}>
          {["REACT_APP_SUPABASE_URL", "REACT_APP_SUPABASE_ANON_KEY"].map((item) => (
            <div key={item} style={S.listItem}>
              <span style={{ fontWeight: 700 }}>{item}</span>
              <span style={S.pageBody}>Set in `.env`</span>
            </div>
          ))}
        </div>

        <ul style={S.setupList}>
          <li>Run the SQL migration in `supabase/migrations/20260407_phase3.sql`.</li>
          <li>Deploy the `employee-admin` and `management-report` edge functions.</li>
          <li>Seed active users and master lists before going live.</li>
        </ul>
      </div>
    </div>
  );
}

export function LoginScreen({ showToast }) {
  const [employeeId, setEmployeeId] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const employeeIdRef = useRef(null);
  const pinRef = useRef(null);

  const handleLogin = async () => {
    if (!employeeId || pin.length !== 4) {
      setError("Enter your employee ID and 4-digit PIN.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await signInWithEmployeeId(employeeId, pin);
      showToast("Signed in successfully.");
    } catch (err) {
      setError(getErrorMessage(err, "Unable to sign in. Please verify your ID and PIN."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.loginShell}>
      <div style={S.loginCard}>
        <div style={S.loginBanner}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={S.brandMark}>SP</div>
            <div>
              <div style={S.pageEyebrow}>
                Company Name
              </div>
              <div style={{ ...S.pageBody, marginTop: 4 }}>
                Production reporting system
              </div>
            </div>
          </div>

          <div style={{ ...S.pageTitle, marginTop: 18 }}>
            Smart Manufacturing Starts Here.
          </div>
          <div style={{ ...S.pageBody, marginTop: 10 }}>
            One platform. Every role. Real-time data from the floor to the boardroom.
          </div>
        </div>

        {error && (
          <div
            style={{
              marginBottom: 16,
              padding: "12px 14px",
              borderRadius: 14,
              background: "#fff1f1",
              color: "#b23a3a",
              fontWeight: 700,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "grid", gap: 14 }}>
          <div
            aria-hidden="true"
            style={autofillTrapStyle}
          >
            <input
              autoComplete="username"
              name="username"
              tabIndex={-1}
              type="text"
            />
            <input
              autoComplete="current-password"
              name="password"
              tabIndex={-1}
              type="password"
            />
          </div>

          <div>
            <label style={S.label}>Employee ID</label>
            <input
              autoCapitalize="characters"
              autoComplete="new-password"
              autoCorrect="off"
              data-1p-ignore="true"
              data-form-type="other"
              data-lpignore="true"
              inputMode="text"
              enterKeyHint="next"
              name="field_a1"
              placeholder="SP001"
              ref={employeeIdRef}
              spellCheck={false}
              style={S.input}
              type="text"
              value={employeeId}
              {...getTapGuardedHandlers(() => {
                activateTapGuardedField(employeeIdRef.current);
              })}
              onChange={(event) => setEmployeeId(event.target.value.toUpperCase())}
              onKeyDown={(event) => event.key === "Enter" && handleLogin()}
            />
          </div>

          <div>
            <label style={S.label}>PIN</label>
            <input
              autoCapitalize="none"
              autoComplete="new-password"
              autoCorrect="off"
              data-1p-ignore="true"
              data-form-type="other"
              data-lpignore="true"
              enterKeyHint="done"
              inputMode="numeric"
              maxLength={4}
              name="field_b1"
              pattern="[0-9]*"
              placeholder="4-digit PIN"
              ref={pinRef}
              spellCheck={false}
              style={{
                ...S.input,
                WebkitTextSecurity: "disc",
                textSecurity: "disc",
              }}
              type="text"
              value={pin}
              {...getTapGuardedHandlers(() => {
                activateTapGuardedField(pinRef.current);
              })}
              onChange={(event) => setPin(event.target.value.replace(/\D/g, ""))}
              onKeyDown={(event) => event.key === "Enter" && handleLogin()}
            />
          </div>

          <button
            disabled={loading}
            onClick={handleLogin}
            style={{ ...S.primaryBtn, opacity: loading ? 0.75 : 1 }}
          >
            {loading ? "Signing In..." : "Sign In"}
          </button>
        </div>

        <div style={{ ...S.pageBody, marginTop: 18 }}>
          User accounts are created by admin. Contact the administrator if you need
          a new employee ID or a PIN reset.
        </div>
      </div>
    </div>
  );
}

const autofillTrapStyle = {
  position: "absolute",
  width: 0,
  height: 0,
  opacity: 0,
  overflow: "hidden",
  pointerEvents: "none",
};
